import classNames from 'classnames';
import { kebabCase } from 'lodash';
import React from 'react';
import { ANNUAL_PERCENTAGE_DISCLAIMER } from 'src/redux/modules/modals/modals.module';
import { useDispatch } from 'react-redux';
import { Fund } from 'src/components/Exchange/types';
import CostBasisSnapshot from 'src/components/Sell/CostBasisSnapshot/CostBasisSnapshot';
import NonCoveredShares from 'src/components/Sell/SellFundEditor/NonCoveredShares/NonCoveredShares';
import WhatWillIPayInTaxes from 'src/components/Sell/SellFundEditor/WhatWillIPayInTaxes';
import CostBasisToolTip from 'src/components/Shared/CostBasisToolTip/CostBasisToolTip';
import ExpandCollapse from 'src/components/Shared/ExpandCollapse/ExpandCollapse';
import If from 'src/components/Shared/If/If';
import MultiInput from 'src/components/Shared/MultiInput/MultiInput';
import config from 'src/config/config';
import { ExchangeTransactionActionType as act } from 'src/redux/modules/exchangeTransaction';
import * as actions from 'src/redux/modules/exchangeTransaction/actions';
import { isCostBasisApplicable, UNIT } from 'src/settings/settings';
import { exchangeFundInputCloseoutWatcher } from 'src/utils/funds/funds-utility';
import { openExternalLinkInPane } from 'src/utils/link-helper';
import styles from './styles.module.scss';

export interface AutoExchangeFromFundEditorProps {
  isSalesforceRep: boolean;
  customerWebViewEnabled: boolean;
  actions: {
    openModal;
    updateExchangeFundUnit: typeof actions.updateExchangeFromFundUnit;
    updateExchangeFund: typeof actions.updateExchangeFund;
    updateExchangeFundAmount: typeof actions.updateExchangeFromFundAmount;
  };
  fund: Fund;
  modalsSeen;
}

export const AutoExchangeFromFundEditor: React.FC<
  AutoExchangeFromFundEditorProps
> = ({
  isSalesforceRep,
  customerWebViewEnabled,
  actions: {
    updateExchangeFundUnit,
    openModal,
    updateExchangeFund,
    updateExchangeFundAmount,
  },
  fund,
  modalsSeen,
}) => {
  const dispatch = useDispatch();
  const {
    agreementID,
    agreementType,
    fundLimitMessage,
    fundName,
    grossAmount,
    grossAmountError,
    grossUnit,
    inFocus,
    ticker,
    selected,
  } = fund;
  const isAll = grossUnit === UNIT.ALL_SHARES;
  const containerStyle = classNames('container');

  if (grossUnit === UNIT.PERCENT && !modalsSeen?.ANNUAL_PERCENTAGE_DISCLAIMER) {
    openModal(ANNUAL_PERCENTAGE_DISCLAIMER);
  }

  const taxableDetails = [
    {
      className: 'collapse1',
      heading: 'How are Noncovered Shares redeemed?',
      show: true,
      content: (
        <NonCoveredShares
          coveredShares={fund.coveredShares}
          isAutomatic={true}
          uncoveredShares={fund.uncoveredShares}
        />
      ), // If this shared component requires changes for Recurring Exchange create a new RecEx copy instead of adapting the shared version
    },
    {
      className: 'collapse2',
      heading: 'What will I pay in taxes?',
      show: !(!customerWebViewEnabled && isSalesforceRep),
      content: <WhatWillIPayInTaxes />, // If this shared component requires changes for Recurring Exchange create a new RecEx copy instead of adapting the shared version
    },
  ];

  return (
    <div
      className={classNames(
        'e2e-recurring-fund-editor',
        styles[containerStyle]
      )}
    >
      <span className={`${styles['input-container']} ${styles['left-panel']}`}>
        <div className={styles['input-label']}>Enter an Amount</div>
        <MultiInput
          ariaLabel={`Recurring Exchange Amount for ${fundName}`}
          autoFocus={selected}
          defaultValue={String(grossAmount)}
          disabled={isAll}
          displayErrorMessage={grossAmountError}
          displayValidationMessage={!grossAmountError}
          error={grossAmountError}
          errorClassName={`e2e-input-error-${kebabCase(ticker)}`}
          selectedInput={isAll ? UNIT.DOLLARS : grossUnit}
          setFocus={inFocus}
          validationMessage={fundLimitMessage}
          onBlur={() => {
            updateExchangeFund(act.UPDATE_EXCHANGE_FROM_FUND, {
              agreementID,
              inFocus: undefined,
            });
          }}
          onInputChange={(amount: number) => {
            updateExchangeFundAmount(agreementID, amount);
            exchangeFundInputCloseoutWatcher(
              amount,
              fund,
              openModal,
              updateExchangeFund
            );
            dispatch(actions.updateGrossAmount(amount));
          }}
          onUnitChange={(unit: UNIT) => {
            updateExchangeFundUnit(agreementID, unit);
            dispatch(actions.selectUnit(unit));
          }}
        />
        <div className={styles['volatility-note']}>
          <div className={'market-volatility-note-box'}>
            <div className={'market-volatility-note'}>
              <span>
                The current value of your fund account, which is based on the
                prior market day's closing prices, maybe higher or lower than
                the closing market value of your fund account at the end of the
                day due to market fluctuation. If you submit a redemption
                request for a dollar amount that exceeds the closing market
                value of your fund account, your transaction will fail and not
                be executed. Avoid this scenario by entering a redemption amount
                as a percentage or number of shares.
              </span>
            </div>
          </div>
        </div>
      </span>
      <If test={isCostBasisApplicable(agreementType)}>
        <div className={styles['cost-basis-container']}>
          <CostBasisSnapshot
            changeAction={() =>
              openExternalLinkInPane(
                `${config.url}/usis/my-accounts/profile/cost-basis`
              )
            }
            customerWebViewEnabled={customerWebViewEnabled}
            fund={fund}
            isSalesforceRep={isSalesforceRep}
            labelComponent={<CostBasisToolTip styles={styles} />}
          />
          <ExpandCollapse
            className="collapse-section"
            details={taxableDetails}
          />
        </div>
      </If>
    </div>
  );
};

export default AutoExchangeFromFundEditor;
